import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  BankIcon,
  Button,
  CashSpecialIcon,
  Layout,
  WalletIcon,
} from "lola-framework-ui-test";
import { FirstSendWarning } from "../../components/first-send/FirstSendWarning";
import { LogoPlaceholder } from "../../components/LogoPlaceholder";
import { Popup } from "../../components/Popup";
import {
  SelectFundingMethodSlot,
  type FlatFundingPick,
} from "../../components/lola/SelectFundingMethodSlot";
import { LOLA_ROUTES, TITLE_COLOR } from "../../constants";
import {
  applyFirstSendSeed,
  FIRST_SEND_SEED_AMOUNT,
  isFirstSendWarningVisible,
} from "../../gallery/firstSendMock";
import { useGalleryFirstSend } from "../../hooks/useGalleryFirstSend";
import { useGalleryPickerLoading } from "../../hooks/useGalleryPickerLoading";
import { useStore } from "../../store/globalState";
import { paymentType } from "../../interfaces/payment";
import { formatBankAccountLabel } from "../../utils/bankAccountLabel";
import type { ICardPaymentMethod } from "../../interfaces/payment-method";
import { mockCards } from "../../gallery/mockData";
import {
  fundRequestAvailableFunding,
  fundRequestFundingDetails,
} from "../../gallery/fundRequestMock";
import { FUNDING_METHOD_CATEGORY, type FundingMethod } from "../../utils/sendCatalog";
import type { UnavailableCardReason } from "../../utils/card-funding-availability";
import masterCardLogo from "../../assets/master_card_icon.svg";
import visaLogo from "../../assets/visa_color_icon.svg";
import { Navbar } from "../../components/Navbar";

/**
 * Certified Lola confirmation / summary chrome (payhub-pages).
 * Gallery-safe: static quote + fund-request Pay with + funding drawers.
 */
const RECIPIENT_FIRST_NAME = "Simón";
const CARD_LAST4 = "1111";

const fundingFromPayment = (
  type: paymentType | undefined
): FundingMethod | null => {
  if (!type) return null;
  switch (type) {
    case paymentType.CARD:
      return "DEBIT_CARD";
    case paymentType.CASH:
      return "CASH_DEPOSIT";
    case paymentType.BANK:
      return "ACH";
    default:
      return null;
  }
};

const RAIL_GALLERY_CARDS: ICardPaymentMethod[] = [
  {
    paymentMethodId: "pm_gallery_rail_rejected",
    last4: "4242",
    brand: "VISA",
    expiryMonth: "12",
    expiryYear: "27",
    cardholderName: "Rail rejected",
    enabled: true,
    isDefault: false,
    fundingMethod: "DEBIT_CARD",
  },
  {
    paymentMethodId: "pm_gallery_rail_temporary",
    last4: "1881",
    brand: "MASTERCARD",
    expiryMonth: "12",
    expiryYear: "27",
    cardholderName: "Rail temporary",
    enabled: true,
    isDefault: false,
    fundingMethod: "DEBIT_CARD",
  },
];

const toSavedCards = (): ICardPaymentMethod[] =>
  mockCards
    .filter(
      (card): card is typeof card & { cardId: string; last4: string; brand: string } =>
        Boolean(card.cardId && card.last4 && card.brand)
    )
    .map(
      (card, index): ICardPaymentMethod => ({
        paymentMethodId: card.cardId,
        last4: card.last4,
        brand: card.brand,
        expiryMonth: "12",
        expiryYear: "27",
        cardholderName: card.fullName ?? undefined,
        enabled: true,
        isDefault: false,
        fundingMethod: index === 0 ? "DEBIT_CARD" : "CREDIT_CARD",
      })
    )
    .concat(RAIL_GALLERY_CARDS);

const RAIL_GALLERY_REASONS: ReadonlyMap<string, UnavailableCardReason> = new Map([
  ["pm_gallery_rail_rejected", "railRejected"],
  ["pm_gallery_rail_temporary", "railTemporarilyUnavailable"],
]);

const defaultCardPayment = () => ({
  cardId: mockCards[0]?.cardId ?? null,
  type: paymentType.CARD,
  brand: "VISA-1111",
});

const FIRST_SEND_SUMMARY = {
  sendAmount: "USD 750.00",
  exchangeRate: "7.6330 GTQ",
  fee: "$ 5.83",
  receiveAmount: "GTQ 5,724.75",
  totalPayment: "$ 755.83",
} as const;

const SUMMARY_VALUES = {
  sendAmount: "USD 400.00",
  exchangeRate: "7.6330 GTQ",
  fee: "$ 3.11",
  receiveAmount: "GTQ 3,053.20",
  fullName: "Simón Arias López",
  destination: "Guatemala",
  pickupAt: "BANRURAL",
  accountNumber: "1000000000",
  totalPayment: "$ 403.11",
} as const;

export const ConfirmationPage = memo(() => {
  const { t } = useTranslation("lola");
  const navigate = useNavigate();
  const firstSend = useGalleryFirstSend();
  const { theme, paymentState, setPaymentState } = useStore();
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    footerColor,
  } = colorConfig;
  const [fundingOpen, setFundingOpen] = useState(false);
  const fundingPickerLoading = useGalleryPickerLoading(fundingOpen);

  const savedCards = useMemo(() => toSavedCards(), []);

  useEffect(() => {
    if (!firstSend) return;
    const card = savedCards[0];
    return applyFirstSendSeed({
      payment: {
        cardId: card?.paymentMethodId ?? null,
        type: paymentType.CARD,
        brand: card ? `${card.brand.toUpperCase()}-${card.last4}` : "VISA-1111",
      },
    });
  }, [firstSend, savedCards]);

  const payment = paymentState.payment ?? defaultCardPayment();
  const selectedFundingMethod = fundingFromPayment(payment.type);
  const cardLast4 = (() => {
    const fromBrand = payment.brand.split("-").pop() || "";
    if (fromBrand === mockCards[0]?.last4) return CARD_LAST4;
    return fromBrand || CARD_LAST4;
  })();

  const summary = firstSend
    ? { ...SUMMARY_VALUES, ...FIRST_SEND_SUMMARY }
    : SUMMARY_VALUES;
  const showFirstSendWarning =
    firstSend != null &&
    isFirstSendWarningVisible({
      sendAmount: firstSend ? Number(FIRST_SEND_SEED_AMOUNT) : 400,
      sourceCurrency: "USD",
      fundingCategory: selectedFundingMethod
        ? FUNDING_METHOD_CATEGORY[selectedFundingMethod]
        : null,
    });

  const rows: Array<{
    label: string;
    value: string;
    strong?: boolean;
  }> = [
    { label: t("confirmation.amountToSend"), value: summary.sendAmount },
    { label: t("confirmation.exchangeRate"), value: summary.exchangeRate },
    { label: t("confirmation.fee"), value: summary.fee },
    {
      label: t("confirmation.receives", { name: RECIPIENT_FIRST_NAME }),
      value: summary.receiveAmount,
    },
    {
      label: t("confirmation.payoutMethod"),
      value: t("confirmation.pickupMethod"),
    },
    { label: t("confirmation.fullName"), value: SUMMARY_VALUES.fullName },
    { label: t("confirmation.destination"), value: SUMMARY_VALUES.destination },
    { label: t("confirmation.pickupAt"), value: SUMMARY_VALUES.pickupAt },
    {
      label: t("confirmation.accountNumber"),
      value: SUMMARY_VALUES.accountNumber,
    },
    {
      label: t("confirmation.totalPayment"),
      value: summary.totalPayment,
      strong: true,
    },
  ];

  const handleFundingPick = useCallback(
    (pick: FlatFundingPick) => {
      if (pick.kind === "add") {
        setFundingOpen(false);
        navigate(LOLA_ROUTES.ADD_CARD);
        return;
      }
      if (pick.kind === "card") {
        setPaymentState({
          payment: {
            cardId: pick.card.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(pick.card.brand || "").toUpperCase()}-${pick.card.last4}`,
          },
        });
        setFundingOpen(false);
        return;
      }
      if (pick.kind === "account") {
        setPaymentState({
          payment: {
            cardId: pick.account.paymentMethodId,
            type: paymentType.BANK,
            brand:
              formatBankAccountLabel(
                pick.account.bankName,
                pick.account.maskedAccountNumber,
              ) ?? pick.account.bankName,
          },
        });
        setFundingOpen(false);
        return;
      }
      if (pick.kind === "cash") {
        setPaymentState({
          payment: {
            cardId: null,
            type: paymentType.CASH,
            brand: t("payment.cashDeposit"),
          },
        });
        setFundingOpen(false);
        return;
      }
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.BANK,
          brand: t("transfer.bankAccount", { last4: "" }).trim(),
        },
      });
      setFundingOpen(false);
    },
    [navigate, setPaymentState, t]
  );

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case paymentType.CARD: {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;
        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case paymentType.BANK:
        return { brand, icon: <BankIcon size={20} /> };
      case paymentType.CASH:
        return { brand, icon: <CashSpecialIcon size={20} /> };
      default:
        return { brand, icon: <WalletIcon size={20} /> };
    }
  }, []);

  const paymentSelected = pickIcon(payment.type, payment.brand);

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable lola-confirmation-page"
      >
        <Layout.Header>
          <Navbar
            color={title}
            title={t("confirmation.title")}
            align="center"
            noBackButton
          />
        </Layout.Header>
        <Layout.Content className="lola-confirmation-section">
          <div className="lola-confirmation-stack">
            <div
              className="lola-confirmation-card"
              style={{ color: footerColor }}
            >
              {rows.map((row) => (
                <div key={row.label} className="lola-confirmation-row">
                  <p
                    className={
                      row.strong
                        ? "lola-confirmation-label lola-confirmation-strong"
                        : "lola-confirmation-label"
                    }
                    style={{ color: TITLE_COLOR }}
                  >
                    {row.label}:
                  </p>
                  <p
                    className={
                      row.strong
                        ? "lola-confirmation-value lola-confirmation-strong"
                        : "lola-confirmation-value"
                    }
                    style={{ color: row.strong ? TITLE_COLOR : footerColor }}
                  >
                    {row.value}
                  </p>
                </div>
              ))}
            </div>

            <section
              className="lola-confirmation-funding"
              style={{ color: footerColor }}
            >
              <span
                className="lola-confirmation-funding-icon"
                style={{ background: colorConfig.backgroundIcon }}
              >
                {paymentSelected.icon}
              </span>
              <p
                className="lola-confirmation-funding-label"
                style={{ color: bodyCopy }}
              >
                {payment.type === paymentType.CARD
                  ? t("confirmation.cardEnding", { last4: cardLast4 })
                  : paymentSelected.brand}
              </p>
              <button
                type="button"
                className="lola-confirmation-funding-change"
                style={{ color: TITLE_COLOR }}
                onClick={() => setFundingOpen(true)}
              >
                {t("confirmation.change")}
              </button>
            </section>
            {showFirstSendWarning ? (
              <FirstSendWarning />
            ) : null}
          </div>

          <section className="lola-confirmation-operated">
            <p
              className="lola-confirmation-operated-label"
              style={{ color: footerColor }}
            >
              {t("confirmation.operatedBy")}
            </p>
            <LogoPlaceholder
              className="lola-confirmation-logo"
              color={footerColor}
            />
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section lola-confirmation-footer">
            <Button
              variant="cancel"
              size="small"
              color={bodyCopy}
              className="lola-confirmation-cancel"
              onClick={() => navigate(LOLA_ROUTES.WELCOME)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => navigate(LOLA_ROUTES.TRANSFERRING)}
            >
              {t("common.pay")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup visible={fundingOpen} onClose={() => setFundingOpen(false)}>
        <SelectFundingMethodSlot
          selectedMethod={selectedFundingMethod}
          selectedPaymentMethodId={
            payment.type === paymentType.CARD ? payment.cardId : null
          }
          available={fundRequestAvailableFunding}
          cards={savedCards}
          loading={fundingPickerLoading}
          details={fundRequestFundingDetails}
          onPick={handleFundingPick}
          onClose={() => setFundingOpen(false)}
          blockedRailCards={RAIL_GALLERY_REASONS}
        />
      </Popup>
    </>
  );
});
